import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Save, MapPin, Phone, Mail, Upload, Image as ImageIcon, Send, Printer } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { compressImage } from '../../utils/imageCompressor';

const MyRestaurantTab = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    bank_name: 'Banco Nacional',
    bank_account: '',
    bank_account_type: 'Corriente',
    bank_account_holder: '',
    logo_url: null,
    // Configuración SMTP para envío de correos
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_name: '',
    // Configuración de impresión
    direct_printing_enabled: false
  });

  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    try {
      const response = await axios.get(`${API}/tenants/my-tenant`, {
        headers: getAuthHeaders()
      });
      
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        bank_name: response.data.bank_name || 'Banco Nacional',
        bank_account: response.data.bank_account || '',
        bank_account_type: response.data.bank_account_type || 'Corriente',
        bank_account_holder: response.data.bank_account_holder || response.data.name,
        logo_url: response.data.logo_url || null,
        smtp_host: response.data.smtp_host || 'smtp.gmail.com',
        smtp_port: response.data.smtp_port || 587,
        smtp_user: response.data.smtp_user || '',
        smtp_password: response.data.smtp_password || '',
        smtp_from_name: response.data.smtp_from_name || response.data.name,
        direct_printing_enabled: response.data.direct_printing_enabled || false
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar información del restaurante'));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona un archivo de imagen');
      return;
    }

    setUploadingLogo(true);
    try {
      // Comprimir en el navegador: redimension a 800x800 y convertir a WebP
      // con el peso necesario. Se acepta CUALQUIER imagen sin tope de tamano.
      const compressedFile = await compressImage(file);

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressedFile);

      const response = await axios.post(`${API}/tenants/upload-logo`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, logo_url: response.data.logo_url });
      toast.success('Logo subido exitosamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al subir logo'));
    }
    setUploadingLogo(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        bank_name: formData.bank_name,
        bank_account: formData.bank_account,
        bank_account_type: formData.bank_account_type,
        bank_account_holder: formData.bank_account_holder || formData.name,
        logo_url: formData.logo_url,
        smtp_host: formData.smtp_host,
        smtp_port: parseInt(formData.smtp_port),
        smtp_from_name: formData.smtp_from_name || formData.name,
        direct_printing_enabled: formData.direct_printing_enabled
      };

      // Solo incluir smtp_user y smtp_password si tienen valores
      // Esto evita sobrescribir con vacíos
      if (formData.smtp_user && formData.smtp_user.trim() !== '') {
        updateData.smtp_user = formData.smtp_user;
      }
      if (formData.smtp_password && formData.smtp_password.trim() !== '') {
        updateData.smtp_password = formData.smtp_password;
      }

      await axios.put(`${API}/tenants/my-tenant`, updateData, {
        headers: getAuthHeaders()
      });

      toast.success('Información actualizada exitosamente');
      
      // Guardar valores SMTP actuales antes de recargar
      const currentSmtpUser = formData.smtp_user;
      const currentSmtpPassword = formData.smtp_password;
      
      // Recargar datos
      await loadTenantData();
      
      // Restaurar valores SMTP en el formulario (no vienen del backend por seguridad)
      setFormData(prev => ({
        ...prev,
        smtp_user: currentSmtpUser,
        smtp_password: currentSmtpPassword
      }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar información'));
    }
    setLoading(false);
  };

  const handleTestEmail = async () => {
    // Validar que haya configuración SMTP
    if (!formData.smtp_user || !formData.smtp_password) {
      toast.error('Por favor configura el email y la contraseña SMTP primero');
      return;
    }

    setTestingEmail(true);
    try {
      const response = await axios.post(
        `${API}/tenants/my-tenant/test-email?test_email=${encodeURIComponent(formData.smtp_user)}`,
        null,
        { headers: getAuthHeaders() }
      );
      
      toast.success(response.data.message || 'Email de prueba enviado exitosamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar email de prueba'));
    }
    setTestingEmail(false);
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Mi Restaurante</h2>
          <p className="text-sm text-gray-600">Configura la información de tu restaurante</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Básica</h3>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Nombre del Restaurante
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre de tu restaurante"
                required
                className="rounded-xl"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Dirección
              </Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Dirección completa"
                required
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Teléfono
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Teléfono de contacto"
                  required
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@restaurante.com"
                  required
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Logo Section */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800">Logo del Restaurante</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Este logo aparecerá en las facturas y documentos oficiales
          </p>
          
          <div className="flex items-center gap-6">
            {formData.logo_url ? (
              <div className="relative">
                <img 
                  src={getImageUrl(formData.logo_url)} 
                  alt="Logo" 
                  className="w-32 h-32 object-contain border-2 border-purple-200 rounded-xl p-3 bg-white shadow-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, logo_url: null })}
                  className="absolute -top-2 -right-2 rounded-full w-7 h-7 p-0 bg-red-500 text-white hover:bg-red-600 border-0"
                >
                  ×
                </Button>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed border-purple-300 rounded-xl flex items-center justify-center bg-white">
                <ImageIcon className="w-12 h-12 text-purple-300" />
              </div>
            )}
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="rounded-xl border-purple-300 hover:bg-purple-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingLogo ? 'Subiendo...' : (formData.logo_url ? 'Cambiar Logo' : 'Subir Logo')}
              </Button>
              <div className="mt-2 text-sm text-gray-600">
                <p>• Formatos: JPG, PNG, GIF, WebP</p>
                <p>• Se comprime y optimiza automaticamente al subirla</p>
                <p>• Recomendado: fondo transparente</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Bank Information */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Datos Bancarios</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Estos datos se mostrarán a los clientes cuando elijan pago por transferencia
          </p>
          
          <div className="space-y-4">
            <div>
              <Label>Nombre del Banco</Label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Ej: Banco Popular, BHD León"
                className="rounded-xl bg-white"
              />
            </div>

            <div>
              <Label>Número de Cuenta</Label>
              <Input
                value={formData.bank_account}
                onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                placeholder="1234-5678-9012-3456"
                className="rounded-xl font-mono bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja vacío si aún no aceptas transferencias
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Cuenta</Label>
                <select
                  value={formData.bank_account_type}
                  onChange={(e) => setFormData({ ...formData, bank_account_type: e.target.value })}
                  className="w-full rounded-xl border border-gray-300 p-2 bg-white"
                >
                  <option value="Corriente">Corriente</option>
                  <option value="Ahorros">Ahorros</option>
                </select>
              </div>

              <div>
                <Label>Titular de la Cuenta</Label>
                <Input
                  value={formData.bank_account_holder}
                  onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                  placeholder="Nombre del titular"
                  className="rounded-xl bg-white"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Printer Configuration */}
        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
          <div className="flex items-center gap-2 mb-4">
            <Printer className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-800">Configuración de Impresión</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-indigo-200">
              <div className="flex-1">
                <Label className="text-base font-semibold text-gray-800">
                  Impresión Automática
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  Cuando está <span className="font-semibold text-green-600">activada</span>, las comandas se envían automáticamente a las impresoras configuradas sin abrir diálogo. 
                  Cuando está <span className="font-semibold text-gray-600">desactivada</span>, se abre el diálogo de impresión del navegador.
                  Los tickets de caja y el reporte de cierre del POS se imprimen directo siempre que el terminal tenga una impresora de estación &quot;POS&quot; asignada; si no la tiene, se abre el diálogo del navegador.
                </p>
              </div>
              <Switch
                checked={formData.direct_printing_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, direct_printing_enabled: checked })}
                className="ml-4"
              />
            </div>
            
            {formData.direct_printing_enabled && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                <p className="text-sm text-green-800">
                  ✅ <span className="font-semibold">Impresión automática activada</span>: Las comandas se enviarán directamente a las impresoras configuradas en la pestaña &quot;Impresoras&quot;.
                </p>
              </div>
            )}
            
            {!formData.direct_printing_enabled && (
              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                <p className="text-sm text-gray-700">
                  ℹ️ <span className="font-semibold">Impresión manual</span>: Se abrirá el diálogo de impresión del navegador para cada comanda. El ticket de caja y el cierre de caja solo se imprimen en silencio si el terminal tiene impresora POS configurada.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Email/SMTP Configuration */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-800">Configuración de Correo Electrónico</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configura tu cuenta de correo para enviar emails de recuperación de contraseña a tus usuarios
          </p>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Servidor SMTP</Label>
                <Input
                  value={formData.smtp_host}
                  onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="rounded-xl bg-white"
                />
              </div>

              <div>
                <Label>Puerto SMTP</Label>
                <Input
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) => setFormData({ ...formData, smtp_port: e.target.value })}
                  placeholder="587"
                  className="rounded-xl bg-white"
                />
                <p className="text-xs text-gray-500 mt-1">
                  587 (TLS) o 465 (SSL)
                </p>
              </div>
            </div>

            <div>
              <Label>Email (Usuario SMTP)</Label>
              <Input
                type="email"
                value={formData.smtp_user}
                onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                placeholder="tucorreo@gmail.com"
                className="rounded-xl bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                Deja vacío para mantener el valor actual
              </p>
            </div>

            <div>
              <Label>Contraseña de Aplicación (App Password)</Label>
              <Input
                type="password"
                value={formData.smtp_password}
                onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                placeholder="••••••••••••••••"
                className="rounded-xl bg-white font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Para Gmail: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Genera una contraseña de aplicación aquí</a>. Deja vacío para mantener el valor actual.
              </p>
            </div>

            <div>
              <Label>Nombre del Remitente</Label>
              <Input
                value={formData.smtp_from_name}
                onChange={(e) => setFormData({ ...formData, smtp_from_name: e.target.value })}
                placeholder={formData.name || "Nombre de tu restaurante"}
                className="rounded-xl bg-white"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este nombre aparecerá en los emails enviados
              </p>
            </div>

            {/* Botón de Prueba */}
            <div className="border-t-2 border-green-200 pt-4 mt-2">
              <div className="bg-green-100 border-l-4 border-green-500 p-4 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-green-800 mb-1">
                      📧 Probar Configuración
                    </p>
                    <p className="text-xs text-green-700">
                      Enviaremos un correo de prueba a: <span className="font-mono font-semibold">{formData.smtp_user || 'tu email SMTP'}</span>
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !formData.smtp_user || !formData.smtp_password}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl min-w-[140px]"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {testingEmail ? 'Enviando...' : 'Enviar Prueba'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MyRestaurantTab;
